import { useMutation, useQuery } from '@apollo/client';
import { Translation, useTranslation } from 'react-i18next';
import React, { useState } from 'react';
import { getGraphQLClient } from 'utils/graphQL/client';
import {
  GET_DOCUMENTS_V2,
  GET_SIGNED_URL,
  GET_SIGNED_URLS,
  GET_DOCUMENT_LINKS,
} from 'utils/graphQL/queries/documentService';
import {
  ATTACH_DOCUMENTS,
  COMBINE_DOCUMENTS,
  DELETE_DOCUMENTS,
  PROCESS_DOCUMENT,
  BULK_PROCESS_DOCUMENT,
  RETRY_UPLOAD_DOCUMENTS,
  UPDATE_DOCUMENT_LINKS,
  DELETE_DOCUMENT_LINKS,
  CREATE_DOCUMENT_LINKS,
  DELETE_FOREIGN_KEY_RELATION_DOCUMENTS,
} from 'utils/graphQL/mutations/documentService';
import { remapData, remapResponseHandler } from 'utils/graphQL/utils';
import { showToast } from '../stores/ToastStore';
import {
  CUSTOM,
  documentTypeNameMap,
  OTHER,
  shipperViewableDocTypesMap,
} from '../config/documents';
import { GET_DOCUMENTS_LIMIT, uploadDocuments } from './DocumentsAPIUtils';
import { areSomeFilesEmpty } from './files/files';
import { FEATURE_FLAG_ENABLED_BREADCRUMB } from 'constants/temp-feature-flag-breadcrumb';
import { UPLOAD_TYPES } from 'constants/Documents';

const DocumentsClient = getGraphQLClient({
  url: DOCUMENT_SERVICE_URL,
});

function useGetDocuments({
  id,
  key,
  foreignKeys,
  type,
  options,
  fetchPolicy,
  groupTypes,
  excludeTypes,
}) {
  let types = type;

  if (type) {
    types = Array.isArray(type) ? type : [type];
  }

  const result = useQuery(GET_DOCUMENTS_V2, {
    client: DocumentsClient,
    variables: {
      query: {
        id,
        key,
        foreignKeys,
        types,
        groupTypes,
        excludeTypes,
      },
      options: {
        limit: GET_DOCUMENTS_LIMIT,
        ...options,
      },
    },
    fetchPolicy,
    skip: !foreignKeys.length,
  });

  return remapData(result, 'getDocumentsV2');
}

function useGetSignedUrls({ foreignKeys }) {
  const result = useQuery(GET_SIGNED_URLS, {
    client: DocumentsClient,
    variables: {
      query: {
        foreignKeys,
      },
    },
    skip: !foreignKeys.length,
  });
  return remapData(result, 'getSignedUrls');
}

function useGetSignedUrl({ key }) {
  const result = useQuery(GET_SIGNED_URL, {
    client: DocumentsClient,
    variables: {
      query: {
        key,
      },
    },
    skip: !key,
  });

  return remapData(result, 'getSignedUrl');
}

function useAttachDocuments() {
  const result = useMutation(ATTACH_DOCUMENTS, {
    client: DocumentsClient,
  });

  return [
    (documentsIds, foreignKeys, uploadType = 'SINGLE', allowAudit = false) =>
      remapResponseHandler(
        result[0],
        'attachDocuments',
      )({ ids: documentsIds, foreignKeys, uploadType, allowAudit }),
    remapData(result[1], 'attachDocuments'),
  ];
}

function useDeleteDocuments() {
  const result = useMutation(DELETE_DOCUMENTS, {
    client: DocumentsClient,
  });

  return [
    (documentsIds) =>
      remapResponseHandler(result[0], 'deleteDocuments')({ ids: documentsIds }),
    remapData(result[1], 'deleteDocuments'),
  ];
}

function useDeleteForeignKeysRelationDocuments() {
  const result = useMutation(DELETE_FOREIGN_KEY_RELATION_DOCUMENTS, {
    client: DocumentsClient,
  });

  return [
    (groupIds, allowAudit = false) =>
      remapResponseHandler(
        result[0],
        'deleteDocumentForeignKeysRelationByFields',
      )({ groupIds, allowAudit }),
    remapData(result[1], 'deleteDocumentForeignKeysRelationByFields'),
  ];
}

function useCombineDocuments() {
  const result = useMutation(COMBINE_DOCUMENTS, {
    client: DocumentsClient,
  });

  return [
    (documentsIds, type, name) =>
      remapResponseHandler(
        result[0],
        'combineDocuments',
      )({ ids: documentsIds, type, name }),
    remapData(result[1], 'combineDocuments'),
  ];
}

function useRetryUploadDocuments() {
  const result = useMutation(RETRY_UPLOAD_DOCUMENTS, {
    client: DocumentsClient,
  });

  return [
    (documentsIds) =>
      remapResponseHandler(
        result[0],
        'retryUploadDocuments',
      )({ ids: documentsIds }),
    remapData(result[1], 'retryUploadDocuments'),
  ];
}

function useValidateDocuments(onValidate, onValidationFailed) {
  const [validating, setValidating] = useState(false);
  const [validFiles, setValidFiles] = useState([]);
  const [invalidPDFs, setInvalidPDFs] = useState([]);

  const validationFailed = async (files, e) => {
    console.error(e);

    showToast(
      <Translation>{(t) => <p>{t(e.message)}</p>}</Translation>,
      <Translation>{(t) => <p>{t(e.message)}</p>}</Translation>,
      'error',
    );

    setValidating(false);
    setValidFiles([]);
    setInvalidPDFs([]);

    return onValidationFailed?.(files, e);
  };

  const handleValidate = async (files) => {
    try {
      setValidating(true);
      setInvalidPDFs([]);

      if (!files.length) {
        return validationFailed(files, new Error('DOCUMENT_VALIDATION_FAILED'));
      }
      if (areSomeFilesEmpty(files)) {
        return validationFailed(files, new Error('SOME_FILES_ARE_EMPTY'));
      }
      const invalidPDFs = [];
      const validFiles = files.filter(
        (file) => !invalidPDFs.includes(file.name),
      );

      setValidating(false);
      setValidFiles(validFiles);
      setInvalidPDFs(invalidPDFs);

      return onValidate(validFiles, invalidPDFs);
    } catch (e) {
      return validationFailed(files, e);
    }
  };

  const resetInvalidPDFs = () => {
    setInvalidPDFs([]);
  };

  return {
    validating,
    validFiles,
    invalidPDFs,
    handleValidate,
    resetInvalidPDFs,
  };
}

function useHandleBulkUploadDocuments(
  shipments,
  stop,
  onUpload,
  onUploadFailed,
) {
  const { t } = useTranslation();
  const [uploading, setUploading] = useState(false);
  const [attachDocuments] = useAttachDocuments();

  const uploadFailed = async (error) => {
    console.error(error);

    if (!error._limit && error.message !== 'PDF_DOCUMENT_ENCRYPTED') {
      showToast(
        t('DOCUMENT_UPLOAD_FAILED_RETRY'),
        t('DOCUMENT_UPLOAD_FAILED_WITH_ERROR', { error }),
        'error',
      );
    }

    return onUploadFailed?.(shipments, stop, error);
  };

  const handleUpload = async (files, type, signatory, allowAudit) => {
    try {
      setUploading(true);

      const userVisibility = [];

      if (shipperViewableDocTypesMap[type]) {
        const docType = shipperViewableDocTypesMap[type];

        if (docType.carrierCanUpload) {
          userVisibility.push('Carrier');
          userVisibility.push('Driver');
        }

        if (docType.shipperCanUpload) {
          userVisibility.push('Shipper');
        }
      } else {
        userVisibility.push('Shipper', 'Carrier', 'Driver');
      }

      const documentsData = await Promise.all(
        files.map(async (file) => {
          const filename =
            type === CUSTOM.value ? file.name : documentTypeNameMap[type];
          return {
            type,
            name: filename,
            signatory,
            content: file,
            contentType: file.type,
            size: file.size,
          };
        }),
      );

      const { data, error } = await uploadDocuments(documentsData);
      if (error) {
        return uploadFailed(error);
      }
      if (shipments && shipments?.length) {
        const documentsIds = data.uploadDocuments.map(
          (document) => document.id,
        );

        const foreignKeys = shipments.map((shipment) => {
          return shipment.id || shipment._id;
        });

        await attachDocuments(
          documentsIds,
          foreignKeys,
          UPLOAD_TYPES.BULK,
          allowAudit,
        );
      }

      return onUpload(
        shipments,
        stop,
        type,
        data.uploadDocuments,
        userVisibility,
      );
    } catch (e) {
      return uploadFailed(e);
    } finally {
      setUploading(false);
    }
  };

  return { uploading, handleUpload };
}

function useHandleUploadDocuments(shipment, stop, onUpload, onUploadFailed) {
  const { t } = useTranslation();
  const [uploading, setUploading] = useState(false);
  const [attachDocuments] = useAttachDocuments();

  const uploadFailed = async (error) => {
    console.error(error);

    if (!error._limit && error.message !== 'PDF_DOCUMENT_ENCRYPTED') {
      showToast(
        t('DOCUMENT_UPLOAD_FAILED_RETRY'),
        t('DOCUMENT_UPLOAD_FAILED_WITH_ERROR', { error }),
        'error',
      );
    }

    return onUploadFailed?.(shipment, stop, error);
  };

  const handleUpload = async (files, type, signatory, allowAudit) => {
    try {
      setUploading(true);

      const userVisibility = [];

      if (shipperViewableDocTypesMap[type]) {
        const docType = shipperViewableDocTypesMap[type];

        if (docType.carrierCanUpload) {
          userVisibility.push('Carrier');
          userVisibility.push('Driver');
        }

        if (docType.shipperCanUpload) {
          userVisibility.push('Shipper');
        }
      } else {
        userVisibility.push('Shipper', 'Carrier', 'Driver');
      }

      const documentsData = await Promise.all(
        files.map(async (file) => {
          const filename =
            type === CUSTOM.value ? file.name : documentTypeNameMap[type];
          return {
            type,
            name: filename,
            signatory,
            content: file,
            contentType: file.type,
            size: file.size,
          };
        }),
      );

      const { data, error } = await uploadDocuments(documentsData);
      if (error) {
        return uploadFailed(error);
      }

      if (shipment) {
        const documentsIds = data.uploadDocuments.map(
          (document) => document.id,
        );

        const foreignKeys = [shipment.id || shipment._id];

        if (stop) {
          foreignKeys.push(stop.id || stop._id);
        }

        await attachDocuments(documentsIds, foreignKeys, undefined, allowAudit);
      }

      return onUpload(
        shipment,
        stop,
        type,
        data.uploadDocuments,
        userVisibility,
      );
    } catch (e) {
      return uploadFailed(e);
    } finally {
      setUploading(false);
    }
  };

  return { uploading, handleUpload };
}

function useHandleUploadDocumentsByForeignKeys(
  foreignKeys,
  onUpload,
  onUploadFailed,
) {
  if (!foreignKeys?.length) {
    throw new Error('FOREIGN_KEYS_REQUIRED');
  }

  const { t } = useTranslation();
  const [uploading, setUploading] = useState(false);
  const [attachDocuments] = useAttachDocuments();

  const uploadFailed = async (error) => {
    console.error(error);

    if (!error._limit && error.message !== 'PDF_DOCUMENT_ENCRYPTED') {
      showToast(
        t('DOCUMENT_UPLOAD_FAILED_RETRY'),
        t('DOCUMENT_UPLOAD_FAILED_WITH_ERROR', { error }),
        'error',
      );
    }

    return onUploadFailed?.(foreignKeys, error);
  };

  const handleUpload = async (files, type, signatory, allowAudit) => {
    try {
      setUploading(true);

      const userVisibility = [];

      if (shipperViewableDocTypesMap[type]) {
        const docType = shipperViewableDocTypesMap[type];

        if (docType.carrierCanUpload) {
          userVisibility.push('Carrier');
          userVisibility.push('Driver');
        }

        if (docType.shipperCanUpload) {
          userVisibility.push('Shipper');
        }
      } else {
        userVisibility.push('Shipper', 'Carrier', 'Driver');
      }

      const documentsData = await Promise.all(
        files.map(async (file) => {
          let filename;
          if (FEATURE_FLAG_ENABLED_BREADCRUMB) {
            const isCustomType = type === CUSTOM.value || type === OTHER.value;
            filename = isCustomType ? file.name : documentTypeNameMap[type];
          } else {
            filename =
              type === CUSTOM.value ? file.name : documentTypeNameMap[type];
          }
          return {
            type,
            name: filename,
            signatory,
            content: file,
            contentType: file.type,
            size: file.size,
          };
        }),
      );

      const { data, error } = await uploadDocuments(documentsData);
      if (error) {
        return uploadFailed(error);
      }

      const documentsIds = data.uploadDocuments.map((document) => document.id);
      await attachDocuments(documentsIds, foreignKeys, undefined, allowAudit);

      return onUpload(foreignKeys, type, data.uploadDocuments, userVisibility);
    } catch (e) {
      return uploadFailed(e);
    } finally {
      setUploading(false);
    }
  };

  return { uploading, handleUpload };
}

function useHandleUpdateDocumentsLink() {
  const [updating, setUpdating] = useState(false);
  const [updateDocumentLink] = useUpdateDocumentLinks();

  const handleUpdateDocumentLink = async (payload, updateDocumentLinkId) => {
    try {
      setUpdating(true);

      const result = await updateDocumentLink({
        variables: { payload, updateDocumentLinkId },
      });
      showToast(
        <Translation>
          {(t) => <p>{t('UPDATE_DOCUMENT_SUCCESS')}</p>}
        </Translation>,
        <Translation>
          {(t) => <p>{t('UPDATE_DOCUMENT_SUCCESS')}</p>}
        </Translation>,
        'success',
      );
      return remapData(result, 'updateDocumentLink');
    } catch (e) {
      showToast(
        <Translation>
          {(t) => <p>{t('UPDATE_DOCUMENT_FAILED')}</p>}
        </Translation>,
        <Translation>
          {(t) => <p>{t('UPDATE_DOCUMENT_FAILED')}</p>}
        </Translation>,
        'error',
      );
    } finally {
      setUpdating(false);
    }
  };
  return { updating, handleUpdateDocumentLink };
}

function useHandleDeleteDocuments(onDelete, onDeleteFailed) {
  const [deleting, setDeleting] = useState(false);
  const [deleteDocuments] = useDeleteDocuments();

  const deleteFailed = async (documents, e) => {
    console.error(e);

    showToast(
      <Translation>{(t) => <p>{t('DOCUMENT_DELETE_FAILED')}</p>}</Translation>,
      <Translation>{(t) => <p>{t('DOCUMENT_DELETE_FAILED')}</p>}</Translation>,
      'error',
    );

    return onDeleteFailed?.(documents, e);
  };

  const handleDelete = async (documents) => {
    try {
      setDeleting(true);

      const deleteResult = await deleteDocuments(
        documents.map((document) => document.id),
      );

      if (deleteResult.error) {
        return deleteFailed(documents, deleteResult.error);
      }

      return onDelete(documents, deleteResult.data);
    } catch (e) {
      return deleteFailed(documents, e);
    } finally {
      setDeleting(false);
    }
  };

  return { deleting, handleDelete };
}

function useHandleDeleteForeignKeyRelationsDocuments(onDelete, onDeleteFailed) {
  const [deleting, setDeleting] = useState(false);
  const [deleteDocuments] = useDeleteForeignKeysRelationDocuments();

  const deleteFailed = async (documents, e) => {
    console.error(e);

    showToast(
      <Translation>{(t) => <p>{t('DOCUMENT_DELETE_FAILED')}</p>}</Translation>,
      <Translation>{(t) => <p>{t('DOCUMENT_DELETE_FAILED')}</p>}</Translation>,
      'error',
    );

    return onDeleteFailed?.(documents, e);
  };

  const handleDelete = async (documents, allowAudit) => {
    try {
      setDeleting(true);
      const groupIds = documents
        .map((doc) => doc?.groupIds || [])
        .flat()
        .filter((groupId) => groupId);
      if (!groupIds?.length) {
        throw new Error('NO_GROUP_ID_FOUND');
      }
      const deleteResult = await deleteDocuments(groupIds, allowAudit);

      if (deleteResult.error) {
        return deleteFailed(documents, deleteResult.error);
      }

      return onDelete(documents, deleteResult.data);
    } catch (e) {
      return deleteFailed(documents, e);
    } finally {
      setDeleting(false);
    }
  };

  return { deleting, handleDelete };
}

function useProcessShipment() {
  const result = useMutation(PROCESS_DOCUMENT, {
    client: DocumentsClient,
  });

  return [
    (documentId, shipmentId, stopId, recognitionOptions) =>
      remapResponseHandler(
        result[0],
        'processDocument',
      )({
        documentId,
        shipmentId,
        stopId,
        recognitionOptions,
      }),
    remapData(result[1], 'processDocument'),
  ];
}

function useBulkProcessShipment() {
  const result = useMutation(BULK_PROCESS_DOCUMENT, {
    client: DocumentsClient,
  });

  return [
    (documentId, shipmentIds, recognitionOptions) =>
      remapResponseHandler(
        result[0],
        'bulkProcessDocument',
      )({
        documentId,
        shipmentIds,
        recognitionOptions,
      }),
    remapData(result[1], 'bulkProcessDocument'),
  ];
}

function useGetDocumentLinks({ foreignKeys }) {
  const result = useQuery(GET_DOCUMENT_LINKS, {
    client: DocumentsClient,
    variables: {
      query: {
        foreignKeys,
      },
      options: {
        limit: GET_DOCUMENTS_LIMIT,
      },
    },
  });
  return remapData(result, 'getDocumentLinks');
}

function useCreateDocumentLinks() {
  const result = useMutation(CREATE_DOCUMENT_LINKS, {
    client: DocumentsClient,
  });

  return [
    (documents) =>
      remapResponseHandler(result[0], 'createDocumentLinks')(documents),
    remapData(result[1], 'createDocumentLinks'),
  ];
}

function useUpdateDocumentLinks() {
  const mutation = useMutation(UPDATE_DOCUMENT_LINKS, {
    client: DocumentsClient,
  });

  return mutation;
}

function useDeleteDocumentLinks() {
  const result = useMutation(DELETE_DOCUMENT_LINKS, {
    client: DocumentsClient,
  });

  return [
    (documentsIds) =>
      remapResponseHandler(
        result[0],
        'deleteDocumentLinks',
      )({ ids: documentsIds }),
    remapData(result[1], 'deleteDocumentLinks'),
  ];
}

export {
  useGetDocuments,
  useGetSignedUrls,
  useGetSignedUrl,
  useAttachDocuments,
  useDeleteDocuments,
  useCombineDocuments,
  useRetryUploadDocuments,
  useValidateDocuments,
  useHandleUploadDocuments,
  useHandleUploadDocumentsByForeignKeys,
  useHandleDeleteDocuments,
  useProcessShipment,
  useCreateDocumentLinks,
  useDeleteDocumentLinks,
  useGetDocumentLinks,
  useHandleUpdateDocumentsLink,
  useHandleBulkUploadDocuments,
  useBulkProcessShipment,
  useHandleDeleteForeignKeyRelationsDocuments,
};
